class ThumbnailsSlider extends HTMLElement {
	constructor() {
		super();
	}
	
	connectedCallback() {
		this.leftButton = this.querySelector('.thumbnails-button--left');
		this.rightButton = this.querySelector('.thumbnails-button--right');
		this.thumbnailWrapper = this.querySelector('.thumbnails-slider__content-wrapper');
		this.thumbnailSlider = this.querySelector('.product-gallery__thumbnail-list');
		
		if (!this.leftButton || !this.rightButton || !this.thumbnailWrapper || !this.thumbnailSlider) {
			return;
		}
		
		this.initButtons();
		this.initCssVariables();
		this.initThumbnailMutationObserver();
		window.addEventListener('resize', this.initCssVariables.bind(this));
	}

	initCssVariables() {
		const thumbnail = this.querySelector('.product-gallery__thumbnail');
		if (!thumbnail) return;
		
		const thumbnailWrapperWidth = this.thumbnailWrapper.offsetWidth;
		const itemWidth = thumbnail.offsetWidth;
		const gapWidthDesktop = (thumbnailWrapperWidth - 6 * itemWidth) / 5;
		const gapWidthMobile = (thumbnailWrapperWidth - 5 * itemWidth) / 4;
		
		if (gapWidthDesktop > 0) {
			this.thumbnailWrapper.style.setProperty('--thumbnail-gap-desktop', `${gapWidthDesktop}px`);
		}
		if (gapWidthMobile > 0) {
			this.thumbnailWrapper.style.setProperty('--thumbnail-gap-mobile', `${gapWidthMobile}px`);
		}
	}

	initButtons() {
		this.leftButton.addEventListener('click', this.scrollToLeft.bind(this));
		this.rightButton.addEventListener('click', this.scrollToRight.bind(this));
		
		if (this.thumbnailSlider.offsetWidth <= this.thumbnailWrapper.offsetWidth) {
			this.hideButton(this.leftButton);
			this.hideButton(this.rightButton);
		}
	}

	initThumbnailMutationObserver() {
		const mutationCallback = (mutationsList, observer) => {
			for (const mutation of mutationsList) {
				if (mutation.type === 'attributes' && mutation.attributeName === 'aria-current') {
					const targetElement = mutation.target;
					if (targetElement.getAttribute('aria-current') === 'true') {
						const thumbnailWrapperRect = this.thumbnailWrapper.getBoundingClientRect();
						const targetElementRect = targetElement.getBoundingClientRect();

						if (targetElementRect.right > thumbnailWrapperRect.right) {
							this.scrollToRight();
						} else if (targetElementRect.left < thumbnailWrapperRect.left) {
							this.scrollToLeft();
						}
					}
				}
			}
		};

		const observer = new MutationObserver(mutationCallback);

		const observerConfig = {
			attributes: true,
			attributeFilter: ['aria-current'],
		};

		const observeItems = this.querySelectorAll('.product-gallery__thumbnail');

		for (const item of observeItems) {
			observer.observe(item, observerConfig);
		}
	}

	hideButton(button) {
		if (button) {
			button.setAttribute('disabled', '');
		}
	}

	scrollToLeft() {
		if (this.leftButton && !this.leftButton.hasAttribute('disabled')) {
			this.thumbnailWrapper.scrollLeft = 0;
			this.leftButton.setAttribute('disabled', '');
			if (this.rightButton) {
				this.rightButton.removeAttribute('disabled');
			}
		}
	}

	scrollToRight() {
		if (this.rightButton && !this.rightButton.hasAttribute('disabled')) {
			this.thumbnailWrapper.scrollLeft = this.thumbnailSlider.offsetWidth - this.thumbnailWrapper.offsetWidth + 1;
			if (this.leftButton) {
				this.leftButton.removeAttribute('disabled');
			}
			this.rightButton.setAttribute('disabled', '');
		}
	}
	
	disconnectedCallback() {
		window.removeEventListener('resize', this.initCssVariables.bind(this));
	}
}

if (!customElements.get('thumbnails-slider')) {
	customElements.define('thumbnails-slider', ThumbnailsSlider);
}

class AutoCloseTabs extends HTMLElement {
	constructor() {
		super();
	}

	connectedCallback() {
		this.tabs = this.querySelectorAll('details');
		if (this.tabs.length === 0) {
			return;
		}
		this.initMutationObserver();
		this.addTabsAttributes('autoclose');
	}

	addTabsAttributes(attribute) {
		this.tabs.forEach((tab) => {
			tab.setAttribute(attribute, '');
		});
	}

	initMutationObserver() {
		const mutationCallback = (mutationsList, observer) => {
			for (const mutation of mutationsList) {
				if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
					const targetElement = mutation.target;
					if (targetElement.open) {
						this.updateTabs(targetElement);
						setTimeout(() => {
							this.scrollIntoView(targetElement);
						}, 200);
					}
				}
			}
		};
		
		const observer = new MutationObserver(mutationCallback);
		const observerConfig = {
			attributes: true,
			attributeFilter: ['open'],
		};
		
		for (const item of this.tabs) {
			observer.observe(item, observerConfig);
		}
	}

	updateTabs(targetElement) {
		this.tabs.forEach((tab) => {
			if (tab !== targetElement && tab.open) {
				// Check if _transition method exists before calling
				if (typeof tab._transition === 'function') {
					tab._open = false;
					tab._transition(false);
				}
				tab.removeAttribute('open');
			}
		});
	}

	scrollIntoView(element) {
		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	}
}

if (!customElements.get('auto-close-tabs')) {
	customElements.define('auto-close-tabs', AutoCloseTabs);
}

// Function to append UTM parameters to links
const appendParamsToLinks = () => {
	const urlSearchParams = new URLSearchParams(window.location.search);
	const utmParams = Array.from(urlSearchParams.entries())
		.filter(([key]) => key.startsWith('utm_'))
		.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
		.join('&');

	if (!utmParams) {
		return; // No UTM parameters to append
	}

	const appendParamsToUrl = (url, params) => {
		if (!url || url === '#') return url;

		try {
			// Handle relative URLs
			const absoluteUrl = new URL(url, window.location.origin);
			const urlSearchParams = new URLSearchParams(absoluteUrl.search);
			const existingParams = new Set();

			urlSearchParams.forEach((_, key) => {
				if (key.startsWith('utm_')) {
					existingParams.add(key);
				}
			});

			const newParams = params
				.split('&')
				.filter((param) => {
					const key = param.split('=')[0];
					return !existingParams.has(decodeURIComponent(key));
				})
				.join('&');

			if (newParams.length === 0) return url;

			return url.includes('?') ? `${url}&${newParams}` : `${url}?${newParams}`;
		} catch (error) {
			return url;
		}
	};

	// Get the elements with the specified classes
	const dynamicUrlElements = document.querySelectorAll('.url-params-update a, .url-params-update');

	// Loop through the elements and append the UTM parameters to their links
	dynamicUrlElements.forEach((element) => {
		const elementLink = element.href;
		if (elementLink && elementLink !== window.location.href) {
			element.href = appendParamsToUrl(elementLink, utmParams);
		}
	});
};

// Run the script only if the page is a Shopify blog post
document.addEventListener('DOMContentLoaded', () => {
	if (window.location.pathname.includes('/blogs/')) {
		appendParamsToLinks();
	}
});

class RichAccordion extends HTMLElement {
	constructor() {
		super();
		this.resizeHandler = null;
	}

	connectedCallback() {
		this.button = this.querySelector('.accordion__toggle--button');
		this.text = this.querySelector('.accordion__content');
		
		if (!this.button || !this.text) {
			return;
		}
		
		this.open = this.hasAttribute('open');
		this.setContentHeight();
		this.setInitialHeight();
		this.initButton(this.button);
		this.setMinHeight();
		
		this.resizeHandler = this.onWidthChange.bind(this);
		window.addEventListener('resize', this.resizeHandler);
	}

	setContentHeight() {
		if (this.text) {
			this.maxHeight = this.text.scrollHeight;
		}
	}

	setMinHeight() {
		if (this.hasAttribute('data-min-height-mobile')) {
			const minHeightMobile = this.convertRemToPixels(parseFloat(this.getAttribute('data-min-height-mobile')));
			this.text.style.setProperty('--accordion-toggle-min-height-mobile', `${minHeightMobile}px`);
		}
		if (this.hasAttribute('data-min-height-desktop')) {
			const minHeightDesktop = this.convertRemToPixels(parseFloat(this.getAttribute('data-min-height-desktop')));
			this.text.style.setProperty('--accordion-toggle-min-height-desktop', `${minHeightDesktop}px`);
		}
	}

	convertRemToPixels(rem) {
		return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
	}

	initButton(button) {
		button.addEventListener('click', this.toggleAccordion.bind(this));
	}

	setInitialHeight() {
		if (!this.text) return;
		
		if (this.open) {
			this.text.style.setProperty('--accordion-toggle-height', `${this.maxHeight}px`);
		} else {
			this.text.style.setProperty('--accordion-toggle-height', '0px');
		}
	}

	onWidthChange() {
		this.setContentHeight();
		this.setInitialHeight();
	}

	toggleAccordion() {
		if (!this.text) return;
		
		if (this.open) {
			this.text.style.setProperty('--accordion-toggle-height', '0px');
			this.open = false;
			this.setAttribute('aria-expanded', 'false');
		} else {
			this.setContentHeight(); // Recalculate before opening
			this.text.style.setProperty('--accordion-toggle-height', `${this.maxHeight}px`);
			this.open = true;
			this.setAttribute('aria-expanded', 'true');
		}
	}
	
	disconnectedCallback() {
		if (this.resizeHandler) {
			window.removeEventListener('resize', this.resizeHandler);
		}
	}
}

if (!customElements.get('rich-accordion')) {
	customElements.define('rich-accordion', RichAccordion);
}

class ScrollingCollapsible extends HTMLElement {
	constructor() {
		super();
		this.resizeHandler = null;
	}

	connectedCallback() {
		if (this.getAttribute('enabled') !== 'true') {
			return;
		}
		
		this.button = this.querySelector('.scrolling-collapsible__button');
		this.text = this.querySelector('.prose');
		
		if (!this.button || !this.text) {
			return;
		}
		
		this.open = this.getAttribute('aria-expanded') === 'true';
		this.setContentHeight();
		this.setInitialHeight();
		this.initButton(this.button);
		this.setMinHeight();
		
		this.resizeHandler = this.onWidthChange.bind(this);
		window.addEventListener('resize', this.resizeHandler);
	}

	setContentHeight() {
		if (this.text) {
			this.maxHeight = this.text.scrollHeight;
		}
	}

	setMinHeight() {
		if (this.hasAttribute('data-min-height-mobile')) {
			const minHeightMobile = this.convertRemToPixels(parseFloat(this.getAttribute('data-min-height-mobile')));
			this.text.style.setProperty('--accordion-toggle-min-height-mobile', `${minHeightMobile}px`);
		}
		if (this.hasAttribute('data-min-height-desktop')) {
			const minHeightDesktop = this.convertRemToPixels(parseFloat(this.getAttribute('data-min-height-desktop')));
			this.text.style.setProperty('--accordion-toggle-min-height-desktop', `${minHeightDesktop}px`);
		}
	}

	convertRemToPixels(rem) {
		return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
	}

	initButton(button) {
		button.addEventListener('click', this.toggleAccordion.bind(this));
	}

	setInitialHeight() {
		if (!this.text) return;
		
		if (this.open) {
			this.text.style.setProperty('--accordion-toggle-height', `${this.maxHeight}px`);
		} else {
			this.text.style.setProperty('--accordion-toggle-height', '0px');
		}
	}

	onWidthChange() {
		this.setContentHeight();
		this.setInitialHeight();
	}

	toggleAccordion() {
		if (!this.text) return;
		
		if (this.open) {
			this.text.style.setProperty('--accordion-toggle-height', '0px');
			this.open = false;
			this.setAttribute('aria-expanded', 'false');
		} else {
			this.setContentHeight(); // Recalculate before opening
			this.text.style.setProperty('--accordion-toggle-height', `${this.maxHeight}px`);
			this.open = true;
			this.setAttribute('aria-expanded', 'true');
		}
	}
	
	disconnectedCallback() {
		if (this.resizeHandler) {
			window.removeEventListener('resize', this.resizeHandler);
		}
	}
}

if (!customElements.get('scrolling-collapsible')) {
	customElements.define('scrolling-collapsible', ScrollingCollapsible);
}

//////////////////////////////////////////////////////
// Kameleoon Cross-Domain Tracking with Pandectes GDPR

(function() {
	'use strict';

	// Configuration
	const CONFIG = {
		debugMode: false, // Set to true for debugging
		kameleoonParamName: 'kam_visitor'
	};

	// Logging helper
	function log(message, data) {
		if (CONFIG.debugMode) {
			console.log(`[Kameleoon GDPR] ${message}`, data !== undefined ? data : '');
		}
	}

	// Initialize Kameleoon queue if not exists
	window.kameleoonQueue = window.kameleoonQueue || [];

	// Track last applied state to prevent duplicate API calls
	let lastAppliedState = undefined;

	// Step 1: Read and set visitor code from URL (BEFORE consent check)
	function setVisitorCodeFromURL() {
		try {
			const urlParams = new URLSearchParams(window.location.search);
			const visitorCodeFromURL = urlParams.get(CONFIG.kameleoonParamName);

			if (visitorCodeFromURL) {
				log('Found visitor code in URL', visitorCodeFromURL);

				// Set visitor code immediately using command queue
				window.kameleoonQueue.push({
					level: 'IMMEDIATE',
					command: function() {
						try {
							if (typeof Kameleoon !== 'undefined' && Kameleoon.API && Kameleoon.API.Visitor) {
								Kameleoon.API.Visitor.setVisitorCode(visitorCodeFromURL);
								log('Visitor code set successfully', visitorCodeFromURL);
								// Clean URL after setting the code
								cleanURLParameter();
							} else {
								log('Kameleoon API not available yet');
							}
						} catch (error) {
							log('Error setting visitor code', error);
						}
					}
				});
			} else {
				log('No visitor code found in URL');
			}
		} catch (error) {
			log('Error in setVisitorCodeFromURL', error);
		}
	}

	// Clean URL parameter after use
	function cleanURLParameter() {
		try {
			const url = new URL(window.location);
			url.searchParams.delete(CONFIG.kameleoonParamName);

			if (url.searchParams.toString() === '') {
				window.history.replaceState({}, document.title, url.pathname);
			} else {
				window.history.replaceState({}, document.title, url.toString());
			}
			log('URL parameter cleaned');
		} catch (error) {
			log('Error cleaning URL parameter', error);
		}
	}

	// Check current Pandectes consent from cookie
	// Returns: number (preferences), or null if no cookie/no decision
	function checkCurrentConsent() {
		try {
			// Look for pandectes_gdpr cookie
			const cookieMatch = document.cookie.match(/pandectes_gdpr=([^;]+)/);

			if (!cookieMatch || !cookieMatch[1]) {
				log('No Pandectes cookie found - consent not given yet');
				return null; // No cookie = no decision made yet
			}

			// Decode base64 and parse JSON
			const decodedValue = atob(cookieMatch[1]);
			const consentData = JSON.parse(decodedValue);

			log('Current Pandectes consent data', consentData);

			// Check if this is a real user decision or just default state
			// Pandectes sets consentType to help identify this
			if (consentData.consentType === 'default' || consentData.status === 'default') {
				log('Pandectes in default state - no user decision yet');
				return null;
			}

			// Check preferences value
			if (typeof consentData.preferences === 'number') {
				return consentData.preferences;
			} else if (consentData.status === 'allow') {
				// If status is allow but no preferences, user accepted all
				return 0;
			} else if (consentData.status === 'deny') {
				// User denied all
				return 7;
			} else {
				// Unknown state - treat as no decision
				log('Unknown Pandectes state - treating as no decision');
				return null;
			}

		} catch (error) {
			log('Error parsing Pandectes cookie', error);
			return null; // Error = no valid decision
		}
	}

	// Apply consent to Kameleoon based on Pandectes preferences
	function applyKameleoonConsent(preferences, source) {
		// Skip if state hasn't changed
		if (lastAppliedState === preferences) {
			log(`No change needed (${source}):`, preferences);
			return;
		}

		// Determine consent state text for logging
		const stateText = preferences === null ? 'UNKNOWN (no decision)' :
						  preferences === 0 ? 'ALL ALLOWED' :
						  preferences === 7 ? 'ALL DENIED' :
						  `PARTIAL (preferences: ${preferences})`;

		log(`Consent state: ${stateText} (source: ${source})`);

		// Only apply consent if we have a definitive decision (not null)
		if (preferences !== null) {
			window.kameleoonQueue.push({
				level: 'NORMAL',
				command: function() {
					try {
						// Check if Kameleoon API is available
						if (typeof Kameleoon === 'undefined' || !Kameleoon.API || !Kameleoon.API.Core) {
							log('Kameleoon API not available');
							return;
						}

						// Check if Performance (2) or Targeting (4) cookies are allowed
						// preferences === 0 means all cookies allowed
						// (preferences & 2) === 0 means Performance allowed
						// (preferences & 4) === 0 means Targeting allowed

						const performanceAllowed = (preferences & 2) === 0;
						const targetingAllowed = (preferences & 4) === 0;
						const hasConsent = preferences === 0 || performanceAllowed || targetingAllowed;

						log('Pandectes preferences', {
							preferences: preferences,
							performanceAllowed: performanceAllowed,
							targetingAllowed: targetingAllowed,
							hasConsent: hasConsent
						});

						if (hasConsent) {
							Kameleoon.API.Core.enableLegalConsent("BOTH");
							log('Kameleoon tracking ENABLED');
						} else {
							Kameleoon.API.Core.disableLegalConsent("BOTH");
							log('Kameleoon tracking DISABLED');
						}

						lastAppliedState = preferences;

					} catch (error) {
						log('Error applying Kameleoon consent', error);
					}
				}
			});
		} else {
			// Null state - Kameleoon will handle this automatically
			// Don't call enable or disable, let Kameleoon stay in null state
			lastAppliedState = null;
			log('Null state - Kameleoon remains in unknown state (no tracking)');
		}
	}

	// Listen for Pandectes consent events
	function listenForConsentChanges() {
		// Primary Pandectes event listener
		window.addEventListener('PandectesEvent_OnConsent', function(event) {
			log('Pandectes consent event received', event.detail);

			if (event.detail) {
				// Check if this is a real user decision
				if (event.detail.consentType === 'default') {
					log('Default consent event - ignoring (no user decision)');
					return;
				}

				if (typeof event.detail.preferences === 'number') {
					applyKameleoonConsent(event.detail.preferences, 'PandectesEvent_OnConsent');
					log('Consent type', event.detail.consentType);
				}
			}
		});

		// Also listen for consent updated events
		window.addEventListener('PandectesEvent_ConsentUpdated', function(event) {
			log('Pandectes consent updated event', event.detail);
			const currentPreferences = checkCurrentConsent();
			applyKameleoonConsent(currentPreferences, 'PandectesEvent_ConsentUpdated');
		});

		// No polling - only event-driven updates
		log('Event listeners registered');
	}

	// Initialize everything
	function initialize() {
		try {
			log('Initializing Pandectes integration');

			// Step 1: Set visitor code from URL immediately
			setVisitorCodeFromURL();

			// Step 2: Check current consent and apply
			// IMPORTANT: This might be null if user hasn't decided
			const currentPreferences = checkCurrentConsent();

			// Only apply if there's an actual decision
			if (currentPreferences !== null) {
				applyKameleoonConsent(currentPreferences, 'initialization');
			} else {
				log('No user consent decision yet - Kameleoon remains in null state');
				lastAppliedState = null;
			}

			// Step 3: Listen for consent changes (event-driven, no polling)
			listenForConsentChanges();

			// Debug info
			log('Debug info:', {
				currentPreferences: currentPreferences,
				cookieExists: document.cookie.includes('pandectes_gdpr'),
				kameleoonDefined: typeof Kameleoon !== 'undefined'
			});
		} catch (error) {
			log('Initialization error:', error);
		}
	}

	// Start initialization
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initialize);
	} else {
		initialize();
	}

})();